import type { MessageDeliveryError } from '../types/templateMessage.ts';
import { mergeMessageDelivery, type MessageDeliveryState } from './messageDelivery.ts';

export interface LastMessageDelivery {
  id?: number;
  lastMessageStatus?: MessageDeliveryState['status'];
  lastMessageFromMe?: boolean;
  lastMessageProviderId?: string | null;
  lastMessageSourceChannel?: string | null;
  lastMessageDeliveryError?: MessageDeliveryError | null;
  lastMessageDeliveryStatusAt?: string | null;
}

const deliveryFields = (contact: LastMessageDelivery): MessageDeliveryState => ({
  status: contact.lastMessageStatus,
  deliveryError: contact.lastMessageDeliveryError,
  deliveryStatusAt: contact.lastMessageDeliveryStatusAt,
});

const contactFields = (state: MessageDeliveryState) => ({
  lastMessageStatus: state.status,
  lastMessageDeliveryError: state.deliveryError,
  lastMessageDeliveryStatusAt: state.deliveryStatusAt,
});

const compatibleChannel = (left?: string | null, right?: string | null) =>
  !left || !right || left === right;

/** Uma consulta iniciada antes do envio pode trazer a prévia da mensagem anterior. */
export const preserveCurrentContactMessage = <T extends LastMessageDelivery & { timestampNumber?: number }>(
  existing: T | undefined, incoming: T,
): T => {
  const previousTime = existing?.timestampNumber;
  const nextTime = incoming.timestampNumber;
  if (!existing || typeof previousTime !== 'number' || !Number.isFinite(previousTime) || previousTime <= 0
    || typeof nextTime !== 'number' || !Number.isFinite(nextTime) || nextTime <= 0 || nextTime >= previousTime) return incoming;
  const preserved: T = { ...incoming };
  const mutable = preserved as Record<string, unknown>;
  const previous = existing as Record<string, unknown>;
  for (const field of ['lastMessage', 'lastMessageType', 'lastMessageStatus', 'lastMessageFromMe', 'lastMessageProviderId',
    'lastMessageSourceChannel', 'lastMessageDeliveryError', 'lastMessageDeliveryStatusAt', 'timestamp', 'timestampNumber', 'last_message_at']) {
    mutable[field] = previous[field];
  }
  return preserved;
};

/** Uma falha pertence à mensagem persistida, nunca ao telefone ou à próxima prévia. */
export const mergeContactMessageDelivery = (
  existing: LastMessageDelivery | undefined,
  incoming: LastMessageDelivery,
) => {
  const sameMessage = existing && incoming.lastMessageFromMe !== false
    && existing.lastMessageFromMe !== false && Boolean(incoming.lastMessageProviderId)
    && existing.lastMessageProviderId === incoming.lastMessageProviderId
    && compatibleChannel(existing.lastMessageSourceChannel, incoming.lastMessageSourceChannel);
  if (!sameMessage) return contactFields(deliveryFields({
    ...incoming,
    lastMessageDeliveryError: incoming.lastMessageFromMe === false ? null : incoming.lastMessageDeliveryError,
  }));
  return contactFields(mergeMessageDelivery(deliveryFields(existing), deliveryFields(incoming)));
};

const providerId = (payload: any): string | null => {
  const id = payload?.providerMessageId || payload?.messageId;
  return typeof id === 'string' && id.length > 0 && id.length <= 255 ? id : null;
};

const eventFields = (payload: any): MessageDeliveryState => ({
  status: ['sending', 'sent', 'delivered', 'read', 'failed'].includes(payload?.status)
    ? payload.status : undefined,
  ack: typeof payload?.ack === 'number' ? payload.ack : undefined,
  deliveryError: payload?.deliveryError,
  deliveryStatusAt: payload?.deliveryStatusAt,
});

export const applyContactMessageStatus = <T extends LastMessageDelivery>(contact: T, payload: any): T => {
  const id = providerId(payload);
  if (!id || contact.lastMessageFromMe === false || contact.lastMessageProviderId !== id
    || !compatibleChannel(contact.lastMessageSourceChannel, payload?.source_channel)) return contact;
  return { ...contact, ...contactFields(mergeMessageDelivery(deliveryFields(contact), eventFields(payload))) };
};

/** Identidade pública do escopo; credenciais nunca entram em chaves ou recibos. */
export const contactDeliveryScope = (): string => typeof localStorage === 'undefined' ? ''
  : ['user_type', 'user_id', 'client_id', 'clinic_id'].map(key => localStorage.getItem(key) || '').join(':');

/** Recibos podem chegar antes da prévia. O buffer é local, limitado e isolado por sessão. */
export class ContactMessageDeliveryInbox {
  private scope = '';
  private entries = new Map<string, { conversationId: string; providerId: string; state: MessageDeliveryState; source_channel?: string; contact_id?: number; receivedAt: number }>();

  private scoped(scope: string) {
    if (this.scope !== scope) { this.scope = scope; this.entries.clear(); }
    const cutoff = Date.now() - 120000;
    this.entries.forEach((entry, key) => { if (entry.receivedAt < cutoff) this.entries.delete(key); });
    return this.entries;
  }

  record(scope: string, conversationId: string, payload: any): void {
    const entries = this.scoped(scope);
    const id = providerId(payload);
    const clinic = typeof localStorage === 'undefined' ? '' : localStorage.getItem('clinic_id');
    if (!conversationId || !id || (payload?.clinic_id != null && clinic
      && String(payload.clinic_id) !== clinic)) return;
    const channel = typeof payload?.source_channel === 'string' ? payload.source_channel : undefined;
    const contactId = Number.isInteger(Number(payload?.contact_id)) && Number(payload.contact_id) > 0 ? Number(payload.contact_id) : undefined;
    const key = `${conversationId}:${id}|${channel || ''}|${contactId || ''}`;
    const previous = entries.get(key);
    entries.set(key, {
      conversationId, providerId: id,
      state: mergeMessageDelivery(previous?.state || {}, eventFields(payload)),
      source_channel: channel,
      contact_id: contactId,
      receivedAt: Date.now(),
    });
    if (entries.size > 128) entries.delete(entries.keys().next().value as string);
  }

  apply<T extends LastMessageDelivery>(scope: string, conversationId: string, contact: T): T {
    let updated = contact;
    this.scoped(scope).forEach(entry => {
      if (entry.conversationId !== conversationId || entry.providerId !== contact.lastMessageProviderId
        || (entry.contact_id && entry.contact_id !== contact.id)) return;
      updated = applyContactMessageStatus(updated, {
        ...entry.state, source_channel: entry.source_channel, providerMessageId: entry.providerId,
      });
    });
    return updated;
  }
}
